import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";

import {
  Box,
  Button,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Tooltip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingBar from 'react-top-loading-bar';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

// Info modal field descriptions
const infoSections = [
  {
    title: 'Page Overview',
    color: '#0B1F3D',
    description:
      'The View Announcements page displays all announcements for the selected branch. Users can search, paginate, view full messages, edit, or delete announcements. Duplicate announcements are automatically removed before display.',
  },
  {
    title: 'Table Columns',
    color: '#1565C0',
    fields: [
      {
        label: 'Class',
        desc: 'The class(es) for which the announcement was created (e.g., "LKG-A1, UKG-B2").',
      },
      {
        label: 'Title',
        desc: 'The short heading of the announcement as entered during creation or editing.',
      },
      {
        label: 'Message',
        desc: 'Shows a "View" button that opens a modal with the full announcement message. HTML tags are stripped before display for clean plain-text rendering.',
      },
      {
        label: 'Created On',
        desc: 'The date and time when the announcement was originally created.',
      },
      {
        label: 'Created For',
        desc: 'The user group(s) (e.g., Students, Teachers, Parents) the announcement was targeted at.',
      },
      {
        label: 'Actions',
        desc: 'Contains Edit and Delete buttons. Edit navigates to the Edit Announcement page with the branch ID passed as a query parameter. Delete opens a confirmation modal before removing the record.',
      },
    ],
  },
  {
    title: 'Search & Pagination',
    color: '#2E7D32',
    fields: [
      {
        label: 'Search',
        desc: 'Filters announcements in real time by Title, Message, or Class name. Case-insensitive matching across all three fields simultaneously.',
      },
      {
        label: 'Rows per Page',
        desc: 'Controls how many announcements are shown per page. Options: 5, 10, 25.',
      },
      {
        label: 'Pagination',
        desc: 'Navigate between pages using the built-in MUI TablePagination controls. Page resets to 0 when rows-per-page is changed.',
      },
    ],
  },
  {
    title: 'Branch & Permissions',
    color: '#6A1B9A',
    fields: [
      {
        label: 'Branch Dropdown',
        desc: 'Visible only to Master users. Allows switching between branches to view announcements scoped to that branch. Non-master users are automatically assigned their own branch from session storage.',
      },
      {
        label: 'Duplicate Removal',
        desc: 'Announcements with identical Title, Message, Class, and Section are deduplicated automatically before being displayed in the table.',
      },
      {
        label: 'Edit Navigation',
        desc: 'The Edit button navigates to /edit-announcement/:id?Branch_Id=... passing the branch ID as a query parameter so the edit form loads with the correct branch context.',
      },
    ],
  },
];

const ViewAnnouncement = () => {
  const navigate = useNavigate();
  const [isMaster, setIsMaster] = useState(false);
const [branches, setBranches] = useState([]);
const [selectedBranchId, setSelectedBranchId] = useState(1);

  const [announcements, setAnnouncements] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
const [viewModalOpen, setViewModalOpen] = useState(false);
const [viewMessage, setViewMessage] = useState('');
const [searchTerm, setSearchTerm] = useState('');
const [showInfoModal, setShowInfoModal] = useState(false); // State for Info Modal

// 🔹 Remove HTML tags coming from backend
const stripHtmlTags = (text) => {
  if (!text) return "";
  return text.replace(/<\/?[^>]+(>|$)/g, "");
};

// Filter announcements based on search term
const filteredAnnouncements = announcements.filter(
  (announcement) =>
    announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.className.toLowerCase().includes(searchTerm.toLowerCase())
);

  // Fetch announcements
 
useEffect(() => {
  const fetchAnnouncements = async () => {
    try {
      setLoadingBarProgress(30);

      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");

      const branchId = isMaster
        ? selectedBranchId
        : sessionStorage.getItem("branchId");

      const response = await fetch(
        `${apiUrl}/Anouncement/All_Anouncement_Admin?Branch_Id=${branchId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      const data = await response.json();
      const announcementList = Array.isArray(data.list) ? data.list : [];
const uniqueAnnouncements = removeDuplicateAnnouncements(announcementList);
setAnnouncements(uniqueAnnouncements);
      setLoadingBarProgress(100);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setLoadingBarProgress(0);
    }
  };

  if (selectedBranchId) {
    fetchAnnouncements();
  }
}, [selectedBranchId, isMaster]);
useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (isMasterUser) {
    fetchBranches();
  } else {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
  }
}, []);
// 🔹 Remove duplicate announcements
const removeDuplicateAnnouncements = (list) => {
  const uniqueMap = new Map();

  list.forEach((item) => {
    const key = `${item.title}-${item.message}-${item.className}-${item.sectiionName}`;
    
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, item);
    }
  });

  return Array.from(uniqueMap.values());
};

  // Handle delete confirmation
  const handleShowDeleteModal = (id, title) => {
    setDeleteId(id);
    setSelectedAnnouncement({ title });
    setShowDeleteModal(true);
  };
  const handleViewMessage = (message) => {
  setViewMessage(message);
  setViewModalOpen(true);
};


const handleDelete = async () => {
  try {
    setLoadingBarProgress(30);
    const response = await fetch(`https://wsms.aidigitalsec.com/api/Anouncement/Id?Id=${deleteId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: sessionStorage.getItem('token'),
      },
    });

    if (response.ok) {
      setModalType('success');
      setModalMessage('Announcement deleted successfully!');
      setAnnouncements(announcements.filter((announcement) => announcement.noticeBoardId !== deleteId));
    } else {
      const errorData = await response.json().catch(() => ({}));
      setModalType('error');
      setModalMessage(`Failed to delete announcement: ${errorData.message || response.statusText}`);
    }
    setModalOpen(true);
    setLoadingBarProgress(100);
    setShowDeleteModal(false);
  } catch (error) {
    console.error('Error deleting announcement:', error);
    setModalType('error');
    setModalMessage('Network error. Please check your connection.');
    setModalOpen(true);
    setLoadingBarProgress(0);
    setShowDeleteModal(false);
  }
};




  // Handle edit
 const handleEdit = (announcement) => {
  navigate(`/edit-announcement/${announcement.noticeBoardId}?Branch_Id=${isMaster ? selectedBranchId : sessionStorage.getItem("branchId")}`, { state: announcement });
};


  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filteredAnnouncements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");

    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LoadingBar color="#0B1F3D" progress={loadingBarProgress} onLoaderFinished={() => setLoadingBarProgress(0)} />
     <AppBar position="static" sx={{ backgroundColor: "#0B1F3D" }}>
  <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
    View Announcements
  </Typography>

  <Tooltip title="View page information & field details" arrow>
    <IconButton
      onClick={() => setShowInfoModal(true)}
      sx={{
        color: '#fff',
        backgroundColor: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.4)',
        borderRadius: '50%',
        width: 34,
        height: 34,
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.28)',
        },
      }}
    >
      <InfoIcon fontSize="small" />
    </IconButton>
  </Tooltip>
</Box>

    {/* Right side: Branch Dropdown + Info Button */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {isMaster && (
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel sx={{ color: "white" }}>Branch</InputLabel>
          <Select
            value={selectedBranchId || ""}
            label="Branch"
            onChange={(e) => setSelectedBranchId(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <LocationOnIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            }
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.3)",
              },
              "& .MuiSvgIcon-root": { color: "white" },
            }}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      
    </Box>

  </Toolbar>
</AppBar>

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'flex-start' }}>

  <TextField
    id="search"
    type="search"
    label="Search"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    sx={{ width: 400 }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <SearchIcon />
        </InputAdornment>
      ),
    }}
  />
</Box>

      <TableContainer component={Paper} sx={{ mt: 1, maxWidth: 1200, }}>
        <Table
          sx={{
            minWidth: 650,
            border: '1px solid #ccc',
            '& .MuiTableCell-root': {
              borderRight: '1px solid #ccc',
              fontSize: '0.9rem',
              padding: '12px',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              {/* Section column removed */}
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Created On</TableCell>
              {/* Created By column removed */}
              <TableCell>Created For</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingBarProgress > 0 && loadingBarProgress < 100 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((announcement) => (
                <TableRow key={announcement.noticeBoardId}>
                  <TableCell>{announcement.className}</TableCell>
                  {/* sectiionName cell removed */}
                  <TableCell>{announcement.title}</TableCell>
                  <TableCell>
  <Button
    variant="outlined"
    size="small"
    onClick={() => handleViewMessage(stripHtmlTags(announcement.message))}

  >
    View
  </Button>
</TableCell>

                  <TableCell>{announcement.createdOn}</TableCell>
                  {/* createdBy cell removed */}
                  <TableCell>{announcement.createdFor}</TableCell>
           <TableCell align="center">
  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
    <Tooltip title="Edit" arrow>
      <Button
  variant="contained"
  color="warning"
  startIcon={<EditIcon />}
  onClick={() => handleEdit(announcement)}
>
  Edit
</Button>

    </Tooltip>

    <Tooltip title="Delete" arrow>
      <Button
        variant="contained"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() =>
          handleShowDeleteModal(
            announcement.noticeBoardId,
            announcement.title
          )
        }
      >
        Delete
      </Button>
    </Tooltip>
  </Box>
</TableCell>

                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No Announcements Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
  rowsPerPageOptions={[5, 10, 25]}
  component="div"
  count={filteredAnnouncements.length}
  rowsPerPage={rowsPerPage}
  page={page}
  onPageChange={handleChangePage}
  onRowsPerPageChange={handleChangeRowsPerPage}
/>

      </TableContainer>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedAnnouncement?.title}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setShowDeleteModal(false)} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="response-modal-title"
        aria-describedby="response-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: modalType === 'success' ? '#0B1F40' : modalType === 'error' ? '#d32f2f' : '#1976d2',
              padding: '12px 16px',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {modalType === 'success' ? 'Success ✅' : modalType === 'error' ? 'Error ❌' : 'Info ℹ️'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              {modalMessage}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setModalOpen(false)}
                variant="contained"
                color={modalType === 'success' ? 'primary' : modalType === 'error' ? 'error' : 'info'}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* View Message Modal */}
      <Modal
  open={viewModalOpen}
  onClose={() => setViewModalOpen(false)}
  aria-labelledby="view-message-modal-title"
  aria-describedby="view-message-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 500,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      p: 4,
    }}
  >
    <Typography variant="h6" sx={{ mb: 2 }}>
      Announcement Message
    </Typography>
    <Typography sx={{ whiteSpace: 'pre-line' }}>
      {viewMessage}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
      <Button
        onClick={() => setViewModalOpen(false)}
        variant="contained"
        color="primary"
      >
        Close
      </Button>
    </Box>
  </Box>
</Modal>

      {/* ── Info Modal ── */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 620 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#90CAF9', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                View Announcements — Page Information
              </Typography>
            </Box>
           
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
            {infoSections.map((section, sIdx) => (
              <Box key={sIdx} sx={{ mb: 3 }}>
                {/* Section Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 22,
                      borderRadius: 1,
                      backgroundColor: section.color,
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: section.color }}>
                    {section.title}
                  </Typography>
                </Box>

                {/* Overview text (no fields) */}
                {section.description && (
                  <Typography variant="body2" sx={{ color: '#555', pl: 1.5 }}>
                    {section.description}
                  </Typography>
                )}

                {/* Field rows */}
                {section.fields && (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    {section.fields.map((field, fIdx) => (
                      <Box key={fIdx}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            px: 2,
                            py: 1.2,
                            backgroundColor: fIdx % 2 === 0 ? '#fafafa' : '#fff',
                          }}
                        >
                          <Chip
                            label={field.label}
                            size="small"
                            sx={{
                              backgroundColor: section.color,
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.72rem',
                              minWidth: 120,
                              borderRadius: '4px',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                            {field.desc}
                          </Typography>
                        </Box>
                        {fIdx < section.fields.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                )}

                {sIdx < infoSections.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              px: 2.5,
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9f9f9',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{ backgroundColor: '#0B1F3D', '&:hover': { backgroundColor: '#163060' } }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

    </Container>
  );
};

export default ViewAnnouncement;